// Packages
import React, { useState, useRef, useEffect } from 'react';

// Services
import formInputValidator from '@PROJECT_ROOT/assets/responsive-frontend/js/services/formInputValidator';
import generateState from '@PROJECT_ROOT/assets/responsive-frontend/js/services/generateStateService';
import userData from '@PROJECT_ROOT/assets/responsive-frontend/js/services/userData';
import {
    apiFetchData,
    apiPostData,
    apiPostDataWithLangISO,
    apiPostUserData,
    getAdriaticLang } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import { getExplicitRedirectType, getExplicitRedirectURL } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/loginRedirectService';
import IterableTrackingService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/iterableTrackingService';
import {
    dispatchTrackingEvent,
    dispatchUserIdentified,
    hasGivenMarketingConsent,
    REGISTER_USER,
    LOGIN_OR_REGISTRATION_FORM_MODAL,
    LOGIN_OR_REGISTRATION_METHOD_EMAIL,
    SIGN_UP,
    LOGIN,
    LOGIN_INSERT_EMAIL_POPUP_VIEW,
    LOGIN_INSERT_EMAIL_POPUP_GOOGLE_CLICK,
    LOGIN_INSERT_EMAIL_POPUP_FACEBOOK_CLICK,
    LOGIN_INSERT_EMAIL_POPUP_SUBMIT,
    LOGIN_INSERT_PASSWORD_POPUP_VIEW,
    LOGIN_INSERT_REGISTRATION_POPUP_VIEW,
    LOGIN_INSERT_PASSWORD_POPUP_FACEBOOK_CLICK,
    LOGIN_INSERT_REGISTRATION_POPUP_FACEBOOK_CLICK,
    LOGIN_INSERT_PASSWORD_POPUP_GOOGLE_CLICK,
    LOGIN_INSERT_REGISTRATION_POPUP_GOOGLE_CLICK,
    LOGIN_INSERT_REGISTRATION_POPUP_SUBMIT,
    LOGIN_INSERT_PASSWORD_POPUP_SUBMIT,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
// Parts
import ModalControl, { ModalBody } from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/modalControl';
import SocialLogin from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/socialLogin';
import Loader from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/loader';
// Components
import TextField from '@PROJECT_ROOT/assets/responsive-frontend/js/formFields/textField';
import CheckBoxField from '@PROJECT_ROOT/assets/responsive-frontend/js/formFields/checkBoxField';
import SelectBoxField from '@PROJECT_ROOT/assets/responsive-frontend/js/formFields/selectBoxField';
import HoneypotField from '@PROJECT_ROOT/assets/responsive-frontend/js/formFields/honeypotField';
// Icons
import { MdOutlineArrowCircleLeft, MdOutlineAccountCircle, MdOutlineGroups, MdOutlineKey } from 'react-icons/md';
import useModalState from '@PROJECT_ROOT/assets/responsive-frontend/js/custom-hooks/useModalState';
import { LOGIN_MODAL_ID } from '@PROJECT_ROOT/assets/responsive-frontend/js/constants/modalIds';

function LoginForMoreBenefits({
    staticData,
    trackingEvents,
    isLoginModalOpenOutsideControl,
    onModalClose,
    singleLoginButtonLayout = false,
}) {
    const langISO = getAdriaticLang();
    const steps = { enterEmail: 1, enterPassword: 2, enterRegistrationData: 3, continueUsingAs: 4 };
    const userEmailCheckPath = 'user-email-check';
    const socialLoginsPathExtension = 'social-logins/';
    const performLoginPath = 'perform-login';
    const performRegistrationPath = 'perform-registration/';
    const initialPathExtension = 'login-registration/';
    const loginFormRef = useRef(null);
    const registerFormRef = useRef(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useModalState(LOGIN_MODAL_ID);
    const [wasLoginModalEverOpen, setWasLoginModalEverOpen] = useState(false);
    const [loginFormData, setLoginFormData] = useState(null);
    const [registrationFormData, setRegistrationFormData] = useState(null);
    const [isEmailSubmit, setIsEmailSubmit] = useState(false);
    const [isLoginSubmit, setIsLoginSubmit] = useState(false);
    const [isRegisterSubmit, setIsRegisterSubmit] = useState(false);
    const [currentStep, setCurrentStep] = useState(steps.enterEmail);
    const [isLoading, setIsLoading] = useState(false);
    const [memorizedEmail, setMemorizedEmail] = useState(null);
    const [publicResources, setPublicResources] = useState(null);
    const [isLoadingPublicResources, setIsLoadingPublicResources] = useState(false);
    const [guestURL, setGuestURL] = useState(null);
    const [partnerInterfaceURL, setPartnerInterfaceURL] = useState(null);
    const [providerInterfaceURL, setProviderInterfaceURL] = useState(null);
    const [isClient, setIsClient] = useState(false);

    const getData = () => {
        setIsLoadingPublicResources(true);
        apiFetchData(initialPathExtension)
            .then((res) => {
                if (res?.status === 200 && res?.data) {
                    setPublicResources(res.data);
                    setLoginFormData(generateState.formStateGenerator(res.data.loginForm.formElements));
                    setRegistrationFormData(generateState.formStateGenerator(res.data.registrationForm.formElements));
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                reportError(err);
            })
            .finally(() => {
                setIsLoadingPublicResources(false);
            });
    };

    const continueToEnterPassword = () => {
        setCurrentStep(steps.enterPassword);
        setMemorizedEmail(loginFormData.email);
        dispatchTrackingEvent(LOGIN_INSERT_EMAIL_POPUP_SUBMIT);
        dispatchTrackingEvent(LOGIN_INSERT_PASSWORD_POPUP_VIEW);
    };

    const continueToEnterRegistrationData = () => {
        setCurrentStep(steps.enterRegistrationData);
        setRegistrationFormData({ ...registrationFormData, email: loginFormData.email });
        dispatchTrackingEvent(LOGIN_INSERT_EMAIL_POPUP_SUBMIT);
        dispatchTrackingEvent(LOGIN_INSERT_REGISTRATION_POPUP_VIEW);
    };

    const backToEnterEmail = () => {
        setCurrentStep(steps.enterEmail);
        setIsEmailSubmit(false);
    };

    const handleSubmitEmail = (e) => {
        e.preventDefault();
        setIsEmailSubmit(true);
        if (memorizedEmail && memorizedEmail === loginFormData.email) {
            continueToEnterPassword();
            return;
        }
        if (!formInputValidator.isRequiredFieldsFilled(loginFormData, publicResources.loginForm.formElements, ['password'], loginFormRef.current)) return;
        setIsLoading(true);
        const postData = {
            twoLetterISOLanguageCode: langISO,
            email: loginFormData.email,
        };
        apiPostData(userEmailCheckPath, postData)
            .then((res) => {
                if (res.data.status === 'success') {
                    continueToEnterPassword();
                } else {
                    continueToEnterRegistrationData();
                }
            })
            .catch((err) => {
                reportError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleLoginFeedbackErrors = (validationErrorMessages) => {
        if (loginFormRef.current) {
            formInputValidator.displayValidationErrorMessages(validationErrorMessages, loginFormRef.current);
        }
    };

    const handleRegisterFeedbackErrors = (validationErrorMessages) => {
        if (registerFormRef.current) {
            formInputValidator.displayValidationErrorMessages(validationErrorMessages, registerFormRef.current);
        }
    };

    const performLogin = (postData) => {
        setIsLoading(true);
        apiPostUserData(performLoginPath, postData)
            .then((res) => {
                if (res.status !== 200) {
                    reportError(res);
                    return;
                }
                if (res.data.validationErrorMessages) {
                    handleLoginFeedbackErrors(res.data.validationErrorMessages);
                    return;
                }
                formInputValidator.removeValidationMessages(loginFormRef.current);
                if (res.data.user) {
                    localStorage.setItem('userJWT', res.data.user);
                    // DL podaci vezani za method email (za google i facebook radimo kroz loadOrCreateFromOAuthProfile u UserProvider.php).
                    const loginRegistrationTrackingData = {
                        method: LOGIN_OR_REGISTRATION_METHOD_EMAIL,
                        form: LOGIN_OR_REGISTRATION_FORM_MODAL,
                    };
                    if (isRegisterSubmit) {
                        dispatchTrackingEvent(SIGN_UP, loginRegistrationTrackingData);
                    } else {
                        dispatchTrackingEvent(LOGIN, loginRegistrationTrackingData);
                    }
                    dispatchUserIdentified(postData.email);
                    if (hasGivenMarketingConsent()) {
                        const IterableData = { email: postData.email };
                        IterableTrackingService.postData(REGISTER_USER, IterableData);
                    }
                }
                const removeHashFromUrl = (url) => {
                    if (!url) return url;
                    const urlObject = new URL(url);
                    urlObject.hash = '';
                    return urlObject.toString();
                };
                setGuestURL(removeHashFromUrl(res.data.redirectURL || window.location.href));
                if (res.data.partnerInterfaceURL || res.data.providersPrivateInterfaceURL) {
                    setPartnerInterfaceURL(removeHashFromUrl(res.data.partnerInterfaceURL));
                    setProviderInterfaceURL(removeHashFromUrl(res.data.providersPrivateInterfaceURL));
                    setCurrentStep(steps.continueUsingAs);
                    return;
                }
                if (res.data.redirectURL) {
                    window.location.href = removeHashFromUrl(res.data.redirectURL);
                } else if (getExplicitRedirectURL()) {
                    // Eksplicitno zadano odredište (npr. na prijavi nakon otkazane prijave preko Facebooka)
                    window.location.href = getExplicitRedirectURL();
                } else {
                    window.location.reload();
                }
            })
            .catch((err) => {
                reportError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleSubmitLogin = (e) => {
        e.preventDefault();
        setIsLoginSubmit(true);
        if (!formInputValidator.isRequiredFieldsFilled(loginFormData, publicResources.loginForm.formElements, [], loginFormRef.current)) return;
        const postData = {
            twoLetterISOLanguageCode: langISO,
            login_type: 'guest',
            ...loginFormData,
        };
        dispatchTrackingEvent(LOGIN_INSERT_PASSWORD_POPUP_SUBMIT);
        performLogin(postData);
    };

    const handleSubmitRegister = (e) => {
        e.preventDefault();
        setIsRegisterSubmit(true);
        if (!formInputValidator.isRequiredFieldsFilled(registrationFormData, publicResources.registrationForm.formElements, [], registerFormRef.current)) return;
        setIsLoading(true);
        const postData = {
            registration_type: 'guest',
            user: userData.getTokenFromLocalStorage(),
            ...registrationFormData,
        };
        apiPostDataWithLangISO(performRegistrationPath, postData)
            .then((res) => {
                if (res.data.validationErrorMessages) {
                    handleRegisterFeedbackErrors(res.data.validationErrorMessages);
                    return;
                }
                formInputValidator.removeValidationMessages(registerFormRef.current);
                if (res.data.status === 'success') {
                    const postDataLogin = {
                        twoLetterISOLanguageCode: langISO,
                        login_type: 'guest',
                        email: registrationFormData.email,
                        password: registrationFormData.password,
                    };
                    dispatchTrackingEvent(LOGIN_INSERT_REGISTRATION_POPUP_SUBMIT);
                    performLogin(postDataLogin);
                }
            })
            .catch((err) => {
                reportError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleLoginInputChange = (e) => {
        setLoginFormData({
            ...loginFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handleRegisterInputChange = (e) => {
        setRegistrationFormData({
            ...registrationFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handleRegisterCheckBox = (e) => {
        setRegistrationFormData({
            ...registrationFormData,
            [e.target.name]: e.target.checked,
        });
    };

    const handleOpenLoginModal = (e) => {
        e?.stopPropagation();
        if (!publicResources) {
            getData();
        }
        setIsLoginModalOpen(true);
        setWasLoginModalEverOpen(true);
        dispatchTrackingEvent(LOGIN_INSERT_EMAIL_POPUP_VIEW);
        if (trackingEvents?.onOpenLoginModal) {
            dispatchTrackingEvent(trackingEvents.onOpenLoginModal);
        }
    };

    useEffect(() => {
        setIsClient(true);
        if (isLoginModalOpenOutsideControl) {
            handleOpenLoginModal();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoginModalOpenOutsideControl]);

    const getModalTitle = () => {
        if (currentStep === steps.enterRegistrationData) {
            return publicResources?.registrationForm?.title;
        }
        if (currentStep === steps.enterPassword) {
            return publicResources?.loginForm?.title;
        }
        return publicResources?.title;
    };

    const handleModalClose = () => {
        if (currentStep === steps.continueUsingAs) {
            window.location.href = guestURL;
            return;
        }
        setIsLoginModalOpen(false);
    };

    useEffect(() => {
        if (!isLoginModalOpen) {
            onModalClose?.();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoginModalOpen]);

    return (
        <>
            {isClient && staticData && (
                singleLoginButtonLayout ? (
                    <span
                        className="login-for-more-benefits__single-login-btn"
                        role="button"
                        tabIndex={0}
                        onClick={handleOpenLoginModal}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleOpenLoginModal();
                            }
                        }}
                    >
                        {staticData.loginButtonText}
                    </span>
                ) : (
                    <div className="login-for-more-benefits d-flex align-items-center">
                        <div className="login-for-more-benefits__content d-flex flex-column">
                            <p className="login-for-more-benefits__content-txt">
                                {staticData.description}
                            </p>
                            <div className="login-for-more-benefits__content-btns d-flex">
                                <button className="adr-btn-link-main" type="button" onClick={handleOpenLoginModal}>
                                    {staticData.loginButtonText}
                                </button>
                                <button className="adr-btn-link-main" type="button" onClick={handleOpenLoginModal}>
                                    {staticData.registrationButtonText}
                                </button>
                            </div>
                        </div>
                        {staticData.icon && (
                            <div>
                                <img
                                    loading="lazy"
                                    src={staticData.icon.sourceURL}
                                    alt={staticData.icon.altText}
                                    title={staticData.icon.titleText}
                                    width="48"
                                    height="48"
                                />
                            </div>
                        )}
                    </div>
                )
            )}
            {wasLoginModalEverOpen && (
                <ModalControl
                    id={LOGIN_MODAL_ID}
                    title={getModalTitle()}
                    isOpen={isLoginModalOpen}
                    disableBodyScroll={true}
                    onClose={handleModalClose}
                    animate={true}
                    leftFooterButton={
                        {
                            content: publicResources?.loginForm?.cancelButtonText,
                            onClick: handleModalClose,
                        }
                    }
                >
                    <ModalBody>
                        {publicResources && loginFormData && registrationFormData && (
                            <div className="login-for-more-benefits__modal-content">
                                {currentStep !== steps.continueUsingAs && (
                                    <>
                                        {currentStep !== steps.enterRegistrationData && (
                                            <div className="login-for-more-benefits__modal-form-container">
                                                {currentStep === steps.enterEmail ? (
                                                    <h2 className="login-for-more-benefits__modal-form-title">
                                                        {publicResources?.title}
                                                    </h2>
                                                ) : (
                                                    <div className="login-for-more-benefits__modal-form-title-container">
                                                        <button
                                                            type="button"
                                                            className="login-for-more-benefits__modal-form-back-button"
                                                            onClick={backToEnterEmail}
                                                            aria-label="Go back to first step"
                                                        >
                                                            <MdOutlineArrowCircleLeft className="login-for-more-benefits__modal-form-back-icon" />
                                                        </button>
                                                        <h2 className="login-for-more-benefits__modal-form-title">{publicResources.loginForm.title}</h2>
                                                    </div>
                                                )}
                                                <form
                                                    ref={loginFormRef}
                                                    className={`form__wrapper ${isLoading ? 'disableCurrentElementContent' : ''}`}
                                                    onSubmit={currentStep === steps.enterEmail ? handleSubmitEmail : handleSubmitLogin}
                                                >
                                                    {publicResources.loginForm.formElements.map((field, index) => {
                                                        switch (field.type) {
                                                            case 'emailField':
                                                                return currentStep === steps.enterEmail ? (
                                                                    <div className="form__article" key={index}>
                                                                        <TextField
                                                                            fieldData={field}
                                                                            currentStateValue={loginFormData[field.name]}
                                                                            onChangeHandler={handleLoginInputChange}
                                                                            controlStateData={{ isSubmit: isEmailSubmit }}
                                                                            inputType="email"
                                                                        />
                                                                    </div>
                                                                ) : null;
                                                            case 'passwordField':
                                                                return currentStep === steps.enterPassword ? (
                                                                    <div className="form__article" key={index}>
                                                                        <TextField
                                                                            fieldData={field}
                                                                            currentStateValue={loginFormData[field.name]}
                                                                            onChangeHandler={handleLoginInputChange}
                                                                            controlStateData={{ isSubmit: isLoginSubmit }}
                                                                            inputType="password"
                                                                        />
                                                                    </div>
                                                                ) : null;
                                                            case 'honeypotField':
                                                                return (
                                                                    <HoneypotField
                                                                        key={field.type + index}
                                                                        fieldData={field}
                                                                        currentStateValue={loginFormData[field.name]}
                                                                        onChangeHandler={handleLoginInputChange}
                                                                        controlStateData={{ isSubmit: isLoginSubmit }}
                                                                    />
                                                                );
                                                            default:
                                                                return null;
                                                        }
                                                    })}
                                                    {currentStep > 1 && (
                                                        <p
                                                            className="login-for-more-benefits__legal-txt"
                                                            dangerouslySetInnerHTML={{ __html: publicResources.legalText }}
                                                        />
                                                    )}
                                                    <button
                                                        className="adr-btn-primary-md"
                                                        type="submit"
                                                    >
                                                        {currentStep === steps.enterEmail ? publicResources.loginForm.formAssets.nextStepText : publicResources.loginForm.submitAction.title}
                                                    </button>
                                                </form>
                                            </div>
                                        )}
                                        {currentStep === steps.enterRegistrationData && (
                                            <div className="login-for-more-benefits__modal-form-container">
                                                <div className="login-for-more-benefits__modal-form-title-container">
                                                    <button
                                                        type="button"
                                                        className="login-for-more-benefits__modal-form-back-button"
                                                        onClick={() => setCurrentStep(steps.enterEmail)}
                                                        aria-label="Go back to first step"
                                                    >
                                                        <MdOutlineArrowCircleLeft className="login-for-more-benefits__modal-form-back-icon" />
                                                    </button>
                                                    <h2 className="login-for-more-benefits__modal-form-title">{publicResources.registrationForm.title}</h2>
                                                </div>
                                                <form
                                                    ref={registerFormRef}
                                                    className={`form__wrapper ${isLoading ? 'disableCurrentElementContent' : ''}`}
                                                    onSubmit={handleSubmitRegister}
                                                >
                                                    {publicResources.registrationForm.formElements.map((field, index) => {
                                                        switch (field.type) {
                                                            case 'textField':
                                                                return (
                                                                    <div className={`form__article ${['firstName', 'lastName'].includes(field.name) ? 'form__article--half-width' : ''}`} key={index}>
                                                                        <TextField
                                                                            fieldData={field}
                                                                            currentStateValue={registrationFormData[field.name]}
                                                                            onChangeHandler={handleRegisterInputChange}
                                                                            controlStateData={{ isSubmit: isRegisterSubmit }}
                                                                            isPhoneField={field.name.startsWith('phone')}
                                                                            selectedCountryCode={field.name.startsWith('phone') ? registrationFormData.countryCode : null}
                                                                        />
                                                                    </div>
                                                                );
                                                            case 'emailField':
                                                                return (
                                                                    <div className="form__article" key={index}>
                                                                        <TextField
                                                                            fieldData={field}
                                                                            currentStateValue={registrationFormData[field.name]}
                                                                            onChangeHandler={handleRegisterInputChange}
                                                                            controlStateData={{ isSubmit: isRegisterSubmit }}
                                                                            inputType="email"
                                                                        />
                                                                    </div>
                                                                );
                                                            case 'passwordField':
                                                                return (
                                                                    <div className={`form__article ${['password', 'confirmPassword'].includes(field.name) ? 'form__article--half-width' : ''}`} key={index}>
                                                                        <TextField
                                                                            fieldData={field}
                                                                            currentStateValue={registrationFormData[field.name]}
                                                                            onChangeHandler={handleRegisterInputChange}
                                                                            controlStateData={{ isSubmit: isRegisterSubmit }}
                                                                            inputType="password"
                                                                        />
                                                                    </div>
                                                                );
                                                            case 'checkBox':
                                                                return (
                                                                    <div className="form__checkbox-alt" key={index}>
                                                                        <CheckBoxField
                                                                            fieldData={field}
                                                                            currentStateValue={registrationFormData[field.name]}
                                                                            controlStateData={{ isSubmit: isRegisterSubmit }}
                                                                            onChangeHandler={handleRegisterCheckBox}
                                                                            size="large"
                                                                        />
                                                                    </div>
                                                                );
                                                            case 'selectBox':
                                                                return (
                                                                    <div className="form__article" key={index}>
                                                                        <SelectBoxField
                                                                            fieldData={field}
                                                                            currentStateValue={registrationFormData[field.name]}
                                                                            onChangeHandler={handleRegisterInputChange}
                                                                            controlStateData={{ isSubmit: isRegisterSubmit }}
                                                                            isCountrySelect={field.name === 'countryCode'}
                                                                        />
                                                                    </div>
                                                                );
                                                            case 'honeypotField':
                                                                return (
                                                                    <HoneypotField
                                                                        key={index}
                                                                        fieldData={field}
                                                                        currentStateValue={registrationFormData[field.name]}
                                                                        onChangeHandler={handleRegisterInputChange}
                                                                        controlStateData={{ isSubmit: isRegisterSubmit }}
                                                                    />
                                                                );
                                                            default:
                                                                return null;
                                                        }
                                                    })}
                                                    <p
                                                        className="login-for-more-benefits__legal-txt"
                                                        dangerouslySetInnerHTML={{ __html: publicResources.legalText }}
                                                    />
                                                    <button
                                                        className="adr-btn-primary-md"
                                                        type="submit"
                                                    >
                                                        {publicResources.registrationForm.submitAction.title}
                                                    </button>
                                                </form>
                                            </div>
                                        )}
                                        <div className="login-for-more-benefits__modal-options">
                                            <p className="login-for-more-benefits__modal-options-social-login-text">{publicResources.loginForm.formAssets.socialLoginText}</p>
                                            {/* Isto odredište kao na stranici za prijavu - eksplicitno zadano kroz URL (npr. na prijavi nakon
                                            otkazane prijave preko Facebooka), inače trenutna stranica, vidi loginRedirectService */}
                                            <SocialLogin
                                                redirectURL={getExplicitRedirectURL() || window.location.href}
                                                redirectType={getExplicitRedirectType()}
                                                socialLoginsPathExtension={socialLoginsPathExtension}
                                                trackingDL={{
                                                    formLoginRegistration: LOGIN_OR_REGISTRATION_FORM_MODAL,
                                                }}
                                                onGoogleLoginClick={() => {
                                                    if (currentStep === steps.enterEmail) {
                                                        dispatchTrackingEvent(LOGIN_INSERT_EMAIL_POPUP_GOOGLE_CLICK);
                                                    } else if (currentStep === steps.enterPassword) {
                                                        dispatchTrackingEvent(LOGIN_INSERT_PASSWORD_POPUP_GOOGLE_CLICK);
                                                    } else if (currentStep === steps.enterRegistrationData) {
                                                        dispatchTrackingEvent(LOGIN_INSERT_REGISTRATION_POPUP_GOOGLE_CLICK);
                                                    }
                                                }}
                                                onFacebookLoginClick={() => {
                                                    if (currentStep === steps.enterEmail) {
                                                        dispatchTrackingEvent(LOGIN_INSERT_EMAIL_POPUP_FACEBOOK_CLICK);
                                                    } else if (currentStep === steps.enterPassword) {
                                                        dispatchTrackingEvent(LOGIN_INSERT_PASSWORD_POPUP_FACEBOOK_CLICK);
                                                    } else if (currentStep === steps.enterRegistrationData) {
                                                        dispatchTrackingEvent(LOGIN_INSERT_REGISTRATION_POPUP_FACEBOOK_CLICK);
                                                    }
                                                }}
                                            />
                                            {(currentStep === 2 || currentStep === 4) && (
                                                <a className="adr-btn-link-main" href={publicResources.loginForm.formAssets.forgotPasswordLinkURL} target="_blank" rel="noreferrer">
                                                    {publicResources.loginForm.formAssets.forgotPasswordLinkText}
                                                </a>
                                            )}
                                        </div>
                                    </>
                                )}
                                {currentStep === steps.continueUsingAs && (
                                    <div className="login-for-more-benefits__modal-form-container">
                                        <p className="login-for-more-benefits__continue-using-as-title">{publicResources.continueUsingAsText.titleText}</p>
                                        <p className="login-for-more-benefits__continue-using-as-subtitle">{publicResources.continueUsingAsText.subtitleText}</p>
                                        <div className="login-for-more-benefits__continue-using-as-buttons">
                                            <a href={guestURL} className="login-for-more-benefits__continue-using-as-button">
                                                <MdOutlineAccountCircle className="login-for-more-benefits__continue-using-as-button-icon continue-using-as-button-icon--blue" />
                                                <span className="login-for-more-benefits__continue-using-as-button-text">{publicResources.continueUsingAsText.guestText}</span>
                                            </a>
                                            {providerInterfaceURL && (
                                                <a href={providerInterfaceURL} className="login-for-more-benefits__continue-using-as-button">
                                                    <MdOutlineKey className="login-for-more-benefits__continue-using-as-button-icon continue-using-as-button-icon--yellow" />
                                                    <span className="login-for-more-benefits__continue-using-as-button-text">{publicResources.continueUsingAsText.providerText}</span>
                                                </a>
                                            )}
                                            {partnerInterfaceURL && (
                                                <a href={partnerInterfaceURL} className="login-for-more-benefits__continue-using-as-button">
                                                    <MdOutlineGroups className="login-for-more-benefits__continue-using-as-button-icon continue-using-as-button-icon--orange" />
                                                    <span className="login-for-more-benefits__continue-using-as-button-text">{publicResources.continueUsingAsText.partnerText}</span>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {!publicResources && isLoadingPublicResources && <Loader />}
                    </ModalBody>
                </ModalControl>
            )}
        </>
    );
}

export default LoginForMoreBenefits;
